// MedioCardGrid.jsx
import React, { useEffect, useState } from 'react';
import parse from "html-react-parser";
import { IconEdit, IconTrash } from "../../../Customiz3r/components/ToolSelectorTop/Icons.jsx";
import { Plus } from "lucide-react";
import axios from "axios";
import { useEditor } from "../../../../App.jsx";
import { capitalize } from "../../../../components/tools/utils.jsx";
import { confirmDelete, showAlert } from "../../../../components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../components/providers/ThemeProvider.jsx";
import { extractColorsFromSVG, determineBackgroundTheme } from './colorUtils';

// Si se pasa `onMedioSelect`, la tarjeta entra en modo "insertar" (usado en el
// explorador del customiz3r): doble clic / botón insertan el medio y se ocultan
// las acciones de editar/borrar (que viven solo en el perfil).
const MedioCardGrid = ({ media, setEditor, setEditorType, setEditorID, onDelete, onMedioSelect }) => {
    const { stateEditor, setStateEditor } = useEditor();
    const theme = getThemeContext();

    const [backgroundTheme, setBackgroundTheme] = useState('light');

    const handleEdit = () => {
        setStateEditor({
            ...stateEditor,
            editor: true,
            editorID: media.id,
            editorType: "medios_svg_input"
        });
    };

    const handleDelete = async () => {
        const confirmed = await confirmDelete({
            title: `¿Eliminar "${media.nombre}"?`,
            text: "Esta acción no se puede deshacer.",
            confirmButtonText: "Eliminar",
            theme,
        });
        if (confirmed) {
            try {
                await axios.delete(`/media/${media.id}/`);
                if (onDelete) onDelete(media.id);
                showAlert({ title: "Eliminado", icon: "success", timer: 1500, showConfirmButton: false, theme });
            } catch (error) {
                console.error('Error al eliminar el medio:', error);
                showAlert({ title: "Error", text: "No se pudo eliminar el medio.", icon: "error", theme });
            }
        }
    };

    function removeWidthAndHeightAttributes(svgString) {
        return svgString.replace(/width="[^"]*"/, '').replace(/height="[^"]*"/, '');
    }

    const analyzeSVGColor = (svgString) => {
        const colors = extractColorsFromSVG(svgString);
        const theme = determineBackgroundTheme(colors);
        setBackgroundTheme(theme);
    };

    useEffect(() => {
        if (media.svg) {
            const sanitizedSVG = removeWidthAndHeightAttributes(media.svg);
            analyzeSVGColor(sanitizedSVG);
        }
    }, [media.svg]);

    return (
        <div
            className="group relative h-56 rounded-lg z-0 shadow-md overflow-hidden
                        transition-all duration-200
                        hover:shadow-xl hover:scale-[1.03] hover:-translate-y-0.5
                        cursor-pointer"
            onDoubleClick={onMedioSelect ? () => onMedioSelect(media) : undefined}
            title={onMedioSelect ? "Doble clic para insertar" : undefined}
        >

            {/* SVG preview area */}
            <div className="h-[80%] w-full flex justify-center items-center
                            relative [&>svg]:w-full [&>svg]:h-full
                            rounded-t-lg overflow-hidden bg-base-200 svg-checker p-2">
                {media.svg && parse(removeWidthAndHeightAttributes(media.svg))}
            </div>

            {/* Centered hover overlay with action buttons */}
            <div className="absolute inset-0 flex items-center justify-center gap-3
                            bg-black/0 group-hover:bg-black/30
                            opacity-0 group-hover:opacity-100
                            transition-all duration-200 z-10 rounded-t-lg">
                {onMedioSelect ? (
                    <button
                        className="btn btn-sm btn-primary shadow-lg gap-1.5
                                   scale-90 group-hover:scale-100 transition-transform duration-200"
                        onClick={() => onMedioSelect(media)}
                        title="Insertar Medio"
                    >
                        <Plus className="w-4 h-4" aria-hidden="true" />
                        Insertar
                    </button>
                ) : (
                    <>
                        {media.id && (
                            <button
                                className="btn btn-sm btn-primary shadow-lg
                                           scale-90 group-hover:scale-100 transition-transform duration-200"
                                onClick={handleEdit}
                                title="Editar Medio"
                            >
                                <IconEdit className="w-4 h-4" aria-hidden="true" />
                            </button>
                        )}
                        <button
                            className="btn btn-sm btn-error shadow-lg
                                       scale-90 group-hover:scale-100 transition-transform duration-200"
                            onClick={handleDelete}
                            title="Eliminar Medio"
                        >
                            <IconTrash className="w-4 h-4" aria-hidden="true" />
                        </button>
                    </>
                )}
            </div>

            {/* Title bar */}
            <div className="h-[20%] border-t border-base-300 bg-base-300
                            flex items-center px-3 rounded-b-lg">
                <h3 className="text-sm font-medium text-base-content w-full text-center
                               truncate leading-none"
                    title={capitalize(media.nombre)}>
                    {capitalize(media.nombre)}
                </h3>
            </div>
        </div>
    );
};

export default MedioCardGrid;
